import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { trackClick, trackClickBlocked } from './tracking';
import { useEarlyAccessPaywallContext } from '../../../packages/EarlyAccessPaywall';
import { ESubscriptionLimitType } from '../../../packages/EarlyAccessPaywall/shared/types/context';
import { IOpenChatPayload, openChat } from '../../actions/chat';
import { EDeviceType } from '../../types/device';
import { TThunkDispatch } from '../../types/redux';
import { IFavoriteEntity } from '../../types/results';

type TOpenChatParams = {
  defaultMessage?: string;
};

interface IParams {
  offer: IFavoriteEntity;
  position: number;
  total: number;
  deviceType: EDeviceType;
}

export function useChats({ offer, position, total, deviceType }: IParams) {
  const dispatch = useDispatch<TThunkDispatch>();
  const earlyAccessContext = useEarlyAccessPaywallContext();
  const isEarlyAccessContactsFlowEnabled = Boolean(offer.isEarlyAccessEnabled && earlyAccessContext);

  const handleEarlyAccessFlow = useCallback(
    async (defaultMessage: string | undefined) => {
      const { checkContactsAccess } = earlyAccessContext;
      const { isConfirmed, trackingStatus } = await checkContactsAccess({
        offerId: offer.entityId,
        limitType: ESubscriptionLimitType.Chat,
      });

      if (isConfirmed) {
        trackClick({ offer, position, total, deviceType, isEarlyAccess: true });
        dispatch(openChat({ offer, position, defaultMessage, isEarlyAccess: true } as IOpenChatPayload));

        return;
      }

      if (trackingStatus) {
        trackClickBlocked({ offer, position, total, deviceType, blockType: trackingStatus });
      }
    },
    [dispatch, earlyAccessContext, offer, position, total, deviceType],
  );

  const handleOpenChat = useCallback(
    async (params: TOpenChatParams | void) => {
      const { defaultMessage } = params || {};

      if (isEarlyAccessContactsFlowEnabled) {
        await handleEarlyAccessFlow(defaultMessage);

        return;
      }

      trackClick({ offer, position, total, deviceType });

      dispatch(openChat({ offer, position, defaultMessage }));
    },
    [dispatch, offer, position, total, deviceType, isEarlyAccessContactsFlowEnabled, handleEarlyAccessFlow],
  );

  return useMemo(
    () => ({
      isEnabled: Boolean(offer.agentInfo?.isChatEnabled),
      openChat: handleOpenChat,
      isLoading:
        earlyAccessContext?.status === 'loading' &&
        earlyAccessContext.lastUsedOfferId === offer.entityId &&
        isEarlyAccessContactsFlowEnabled,
    }),
    [offer, handleOpenChat, earlyAccessContext, isEarlyAccessContactsFlowEnabled],
  );
}
